import type { TemplateResult } from "lit";
import { html } from "lit";
import { ifDefined } from "lit/directives/if-defined.js";
import type { Breadcrumb } from "@ligonier/shared";
import { sanitizeHref } from "@ligonier/shared";

const stripTrailingSlash = (pathname: string): string =>
  pathname.replace(/\/+$/, "") || "/";

/**
 * True when a crumb's href resolves to the page currently being viewed.
 * Breadcrumb links are site-local, so relative hrefs resolve against the
 * current origin (matching browser behavior for the rendered anchor).
 */
function isCurrentPage(href: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const resolved = new URL(href, window.location.href);
    return (
      resolved.origin === window.location.origin &&
      stripTrailingSlash(resolved.pathname) ===
        stripTrailingSlash(window.location.pathname)
    );
  } catch {
    return false;
  }
}

export function renderBreadcrumbs(breadcrumbs: Array<Breadcrumb>): TemplateResult {
  return html`
    <div class="breadcrumbs">
      <div class="container container-sm">
        <nav class="breadcrumbs-nav" aria-label="Breadcrumb">
          ${breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            const separator =
              index > 0
                ? html`<span class="breadcrumb-separator" aria-hidden="true">/</span>`
                : "";

            // Any crumb with a link renders as a link — including the last one,
            // matching the previous (React) footer, whose trails often end in a
            // linked crumb (e.g. the homepage's single "Home" crumb). Crumbs
            // without a link render as plain text.
            //
            // aria-current="page" is only announced when the crumb actually
            // represents the page being viewed: a link-less final crumb, or a
            // final link that resolves to the current URL. (Some apps end the
            // trail with a parent link — e.g. "Postings" on a posting-detail
            // page — which must not claim to be the current page.)
            if (crumb.link) {
              // sanitizeHref (not sanitizeHeaderHref): breadcrumb links are
              // site-local, so relative paths must stay on the current origin
              // rather than being rebased onto www.ligonier.org.
              const href = sanitizeHref(crumb.link);
              const ariaCurrent =
                isLast && isCurrentPage(href) ? "page" : undefined;
              return html`${separator}<a
                  href="${href}"
                  class="breadcrumb-link"
                  aria-current="${ifDefined(ariaCurrent)}"
                  >${crumb.label}</a
                >`;
            }

            return html`${separator}<span
                class="breadcrumb-current"
                aria-current="${ifDefined(isLast ? "page" : undefined)}"
                >${crumb.label}</span
              >`;
          })}
        </nav>
      </div>
    </div>
  `;
}
